import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, FileText, Crown, Check } from 'lucide-react';
import { rb } from './theme';
import { resumeService } from '../../services/resumeService';

export default function TemplatePicker({ onSelect, starting }) {
  const [templates, setTemplates] = useState(null);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await resumeService.getTemplates();
        const list = Array.isArray(data) ? data : [];
        setTemplates(list);
        if (list.length) setSelected(list[0].id);
      } catch {
        setError("Couldn't load templates. Please refresh and try again.");
        setTemplates([]);
      }
    })();
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4">
      <h2 className="text-xl md:text-2xl font-bold mb-1 text-slate-100">Pick a style</h2>
      <p className="text-sm mb-6 text-slate-400">
        Every style is ATS-friendly — recruiters' software can read them all. Just pick the look you like.
      </p>

      {templates === null ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => {
            const isSel = selected === t.id;
            return (
              <motion.button
                key={t.id}
                type="button"
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelected(t.id)}
                className={`relative text-left p-4 rounded-2xl transition-all ${isSel ? `${rb.glass} ${rb.gradRing}` : `${rb.glassSoft} hover:border-violet-400/30`}`}
              >
                {isSel && (
                  <span className={`absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center z-10 ${rb.glowBtn}`}>
                    <Check className="w-3.5 h-3.5 text-white" />
                  </span>
                )}
                {t.preview_image_url ? (
                  <img src={t.preview_image_url} alt={t.name} className="w-full h-44 object-cover object-top rounded-xl mb-3" />
                ) : (
                  <div className="w-full h-44 rounded-xl mb-3 flex items-center justify-center" style={{ background: 'linear-gradient(160deg, rgba(124,58,237,0.22), rgba(37,99,235,0.14))' }}>
                    <FileText className="w-10 h-10 text-violet-300/50" />
                  </div>
                )}
                <h3 className="font-bold text-slate-100">{t.name}</h3>
                {t.description && <p className="text-xs mt-1 text-slate-400">{t.description}</p>}
                {t.is_premium && (
                  <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-300 text-[11px] font-bold">
                    <Crown className="w-3 h-3" /> Premium
                  </span>
                )}
              </motion.button>
            );
          })}
        </div>
      )}

      {error && <p className="mt-4 text-sm text-red-400">{error}</p>}

      <button
        type="button"
        onClick={() => onSelect(selected)}
        disabled={!selected || starting}
        className={`mt-8 w-full sm:w-auto sm:px-10 flex items-center justify-center gap-2 py-3.5 rounded-2xl ${rb.glowBtn}`}
      >
        {starting && <Loader2 className="w-4 h-4 animate-spin" />}
        {starting ? 'Starting…' : 'Continue →'}
      </button>
    </div>
  );
}
